import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { toastError } from "@/lib/toastError";
import { Trash2, Plus, Save, Send, Users, Gavel, CalendarCheck, Inbox } from "lucide-react";

const EMPTY_MEMBER = { name: "", role: "Member", credentials: "", country: "", since: "2026", bio: "", expertise: "", order: 99, active: true };
const EMPTY_RULING = { slug: "", topic: "", verdict: "Permissible", question: "", ruling: "", basis: "", date: new Date().toISOString().slice(0, 10), published: true };
const EMPTY_MEETING = { date: new Date().toISOString().slice(0, 10), title: "", quorum: "", summary: "", decisions: "" };

const Section = ({ icon: Icon, title, count, children }) => (
  <Card className="p-5 shadow-premium">
    <div className="flex items-center gap-2 text-sm font-semibold mb-4">
      <Icon className="h-4 w-4 text-[#3E2E65]" />{title}
      {count !== undefined && <Badge variant="outline" className="text-[10px]">{count}</Badge>}
    </div>
    {children}
  </Card>
);

export const ShariahBoardAdmin = () => {
  const [data, setData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [member, setMember] = useState(EMPTY_MEMBER);
  const [ruling, setRuling] = useState(EMPTY_RULING);
  const [meeting, setMeeting] = useState(EMPTY_MEETING);
  const [answers, setAnswers] = useState({});

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/admin/shariah-board");
      setData(d);
      setSettings({ ...d.settings, standards: (d.settings.standards || []).join(", ") });
    } catch (e) {
      toastError(e, "Could not load the board");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    try {
      await api.put("/admin/shariah-board/settings", {
        ...settings,
        standards: settings.standards.split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Board details published");
      load();
    } catch (e) { toastError(e, "Save failed"); }
  };

  const addMember = async () => {
    try {
      await api.post("/admin/shariah-board/members", {
        ...member, order: Number(member.order) || 99,
        expertise: member.expertise.split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success(`${member.name} added to the board`);
      setMember(EMPTY_MEMBER); load();
    } catch (e) { toastError(e, "Could not add the member"); }
  };

  const addRuling = async () => {
    try {
      await api.post("/admin/shariah-board/rulings", ruling);
      toast.success("Ruling published");
      setRuling(EMPTY_RULING); load();
    } catch (e) { toastError(e, "Could not publish the ruling"); }
  };

  const addMeeting = async () => {
    try {
      await api.post("/admin/shariah-board/meetings", {
        ...meeting, decisions: meeting.decisions.split("\n").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Meeting recorded");
      setMeeting(EMPTY_MEETING); load();
    } catch (e) { toastError(e, "Could not record the meeting"); }
  };

  const remove = async (kind, id, label) => {
    try {
      await api.delete(`/admin/shariah-board/${kind}/${id}`);
      toast.success(`${label} removed`);
      load();
    } catch (e) { toastError(e, "Delete failed"); }
  };

  const answer = async (q) => {
    const a = answers[q.id] || {};
    if (!a.text || a.text.length < 10) return toast.error("Write the board's answer first");
    try {
      const { data: res } = await api.post(`/admin/shariah-board/questions/${q.id}/answer`, {
        answer: a.text, publish_as_ruling: !!a.publish,
        slug: a.publish ? (a.slug || `ruling-${q.id.slice(0, 8)}`) : null,
        topic: q.topic, verdict: "Guidance",
      });
      toast.success(res.published_ruling ? "Answered and published as a ruling" : "Answer emailed to the client");
      setAnswers({ ...answers, [q.id]: {} });
      load();
    } catch (e) { toastError(e, "Could not send the answer"); }
  };

  if (!data || !settings) return <p className="text-sm text-muted-foreground">Loading the board…</p>;
  const open = data.questions.filter((q) => q.status === "open");

  return (
    <div className="space-y-4" data-testid="admin-shariah-board">
      <Section icon={Save} title="Certificate & mandate">
        <div className="grid sm:grid-cols-3 gap-3">
          {[["board_name", "Board name"], ["certificate_id", "Certificate ID"], ["review_cycle", "Review cycle"],
            ["last_audit", "Last audit (YYYY-MM-DD)"], ["next_review", "Next review (YYYY-MM-DD)"],
            ["standards", "Standards (comma separated)"]].map(([k, label]) => (
            <div key={k}>
              <label className="text-[11px] text-muted-foreground">{label}</label>
              <Input value={settings[k] || ""} onChange={(e) => setSettings({ ...settings, [k]: e.target.value })}
                className="mt-1 h-9 text-sm" data-testid={`board-settings-${k}`} />
            </div>
          ))}
        </div>
        <Textarea rows={2} value={settings.statement || ""} className="mt-3 text-sm"
          onChange={(e) => setSettings({ ...settings, statement: e.target.value })}
          placeholder="Certificate statement" data-testid="board-settings-statement" />
        <Textarea rows={2} value={settings.mandate || ""} className="mt-3 text-sm"
          onChange={(e) => setSettings({ ...settings, mandate: e.target.value })}
          placeholder="Board mandate (shown on the public page)" data-testid="board-settings-mandate" />
        <Button size="sm" className="mt-3" onClick={saveSettings} data-testid="board-settings-save">
          <Save className="h-4 w-4 me-1.5" />Publish changes
        </Button>
      </Section>

      <Section icon={Users} title="Board members" count={data.members.length}>
        <div className="space-y-2">
          {data.members.map((m) => (
            <div key={m.id} className="flex items-center gap-3 rounded-xl border p-3" data-testid={`admin-member-${m.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold truncate">{m.name}</div>
                <div className="text-[11px] text-muted-foreground truncate">{m.role} · {m.credentials} · {m.country}</div>
              </div>
              {!m.active && <Badge variant="outline" className="text-[10px]">hidden</Badge>}
              <Button variant="ghost" size="sm" onClick={() => remove("members", m.id, m.name)}
                data-testid={`admin-member-delete-${m.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
          ))}
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-2">
          {[["name", "Full name"], ["role", "Role"], ["credentials", "Credentials"], ["country", "Country"],
            ["since", "Member since"], ["expertise", "Expertise (comma separated)"]].map(([k, ph]) => (
            <Input key={k} placeholder={ph} value={member[k]} className="h-9 text-sm"
              onChange={(e) => setMember({ ...member, [k]: e.target.value })} data-testid={`new-member-${k}`} />
          ))}
        </div>
        <Textarea rows={2} placeholder="Short biography" value={member.bio} className="mt-2 text-sm"
          onChange={(e) => setMember({ ...member, bio: e.target.value })} data-testid="new-member-bio" />
        <Button size="sm" className="mt-3" onClick={addMember} disabled={!member.name}
          data-testid="new-member-save"><Plus className="h-4 w-4 me-1.5" />Add member</Button>
      </Section>

      <Section icon={Gavel} title="Rulings" count={data.rulings.length}>
        <div className="space-y-2 max-h-72 overflow-y-auto pe-1">
          {data.rulings.map((r) => (
            <div key={r.id} className="flex items-center gap-3 rounded-xl border p-3" data-testid={`admin-ruling-${r.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{r.question}</div>
                <div className="text-[11px] text-muted-foreground">{r.topic} · {r.verdict} · {r.date}{r.published ? "" : " · draft"}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove("rulings", r.id, "Ruling")}
                data-testid={`admin-ruling-delete-${r.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
          ))}
        </div>
        <div className="mt-4 grid sm:grid-cols-4 gap-2">
          <Input placeholder="slug" value={ruling.slug} className="h-9 text-sm"
            onChange={(e) => setRuling({ ...ruling, slug: e.target.value })} data-testid="new-ruling-slug" />
          <Input placeholder="Topic" value={ruling.topic} className="h-9 text-sm"
            onChange={(e) => setRuling({ ...ruling, topic: e.target.value })} data-testid="new-ruling-topic" />
          <Input placeholder="Verdict" value={ruling.verdict} className="h-9 text-sm"
            onChange={(e) => setRuling({ ...ruling, verdict: e.target.value })} data-testid="new-ruling-verdict" />
          <Input placeholder="YYYY-MM-DD" value={ruling.date} className="h-9 text-sm"
            onChange={(e) => setRuling({ ...ruling, date: e.target.value })} data-testid="new-ruling-date" />
        </div>
        <Input placeholder="The question asked" value={ruling.question} className="mt-2 h-9 text-sm"
          onChange={(e) => setRuling({ ...ruling, question: e.target.value })} data-testid="new-ruling-question" />
        <Textarea rows={3} placeholder="The board's ruling" value={ruling.ruling} className="mt-2 text-sm"
          onChange={(e) => setRuling({ ...ruling, ruling: e.target.value })} data-testid="new-ruling-body" />
        <Input placeholder="Basis (standard or principle)" value={ruling.basis} className="mt-2 h-9 text-sm"
          onChange={(e) => setRuling({ ...ruling, basis: e.target.value })} data-testid="new-ruling-basis" />
        <div className="mt-3 flex items-center gap-3">
          <Button size="sm" onClick={addRuling} disabled={!ruling.slug || !ruling.question}
            data-testid="new-ruling-save"><Plus className="h-4 w-4 me-1.5" />Publish ruling</Button>
          <div className="flex items-center gap-2 text-xs">
            <Switch checked={ruling.published} onCheckedChange={(v) => setRuling({ ...ruling, published: v })}
              data-testid="new-ruling-published" />Visible to clients
          </div>
        </div>
      </Section>

      <Section icon={CalendarCheck} title="Meeting record" count={data.meetings.length}>
        <div className="space-y-2">
          {data.meetings.map((m) => (
            <div key={m.id} className="flex items-center gap-3 rounded-xl border p-3" data-testid={`admin-meeting-${m.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold">{m.title} <span className="text-[11px] text-muted-foreground numeric">{m.date}</span></div>
                <div className="text-[11px] text-muted-foreground truncate">{m.summary}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => remove("meetings", m.id, "Meeting")}
                data-testid={`admin-meeting-delete-${m.id}`}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
            </div>
          ))}
        </div>
        <div className="mt-4 grid sm:grid-cols-3 gap-2">
          <Input placeholder="YYYY-MM-DD" value={meeting.date} className="h-9 text-sm"
            onChange={(e) => setMeeting({ ...meeting, date: e.target.value })} data-testid="new-meeting-date" />
          <Input placeholder="Title" value={meeting.title} className="h-9 text-sm"
            onChange={(e) => setMeeting({ ...meeting, title: e.target.value })} data-testid="new-meeting-title" />
          <Input placeholder="Quorum (e.g. 3 of 3)" value={meeting.quorum} className="h-9 text-sm"
            onChange={(e) => setMeeting({ ...meeting, quorum: e.target.value })} data-testid="new-meeting-quorum" />
        </div>
        <Textarea rows={2} placeholder="Summary" value={meeting.summary} className="mt-2 text-sm"
          onChange={(e) => setMeeting({ ...meeting, summary: e.target.value })} data-testid="new-meeting-summary" />
        <Textarea rows={3} placeholder="Decisions — one per line" value={meeting.decisions} className="mt-2 text-sm"
          onChange={(e) => setMeeting({ ...meeting, decisions: e.target.value })} data-testid="new-meeting-decisions" />
        <Button size="sm" className="mt-3" onClick={addMeeting} disabled={!meeting.title}
          data-testid="new-meeting-save"><Plus className="h-4 w-4 me-1.5" />Record meeting</Button>
      </Section>

      <Section icon={Inbox} title="Questions from clients" count={`${open.length} open`}>
        {data.questions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No questions yet.</p>
        ) : (
          <div className="space-y-3">
            {data.questions.map((q) => (
              <div key={q.id} className="rounded-xl border p-4" data-testid={`admin-question-${q.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold">{q.name}</span>
                  <span className="text-[11px] text-muted-foreground">{q.email}</span>
                  <Badge variant="outline" className="text-[10px]">{q.topic}</Badge>
                  <Badge variant="outline" className={`text-[10px] ${q.status === "open" ? "text-amber-700 border-amber-200 bg-amber-50" : "text-emerald-700 border-emerald-200 bg-emerald-50"}`}>{q.status}</Badge>
                </div>
                <p className="mt-2 text-sm">{q.question}</p>
                {q.answer ? (
                  <p className="mt-2 text-xs text-muted-foreground border-s-2 border-[#3E2E65] ps-3">{q.answer}</p>
                ) : (
                  <>
                    <Textarea rows={3} className="mt-3 text-sm" placeholder="The board's answer — this is emailed to the client"
                      value={answers[q.id]?.text || ""}
                      onChange={(e) => setAnswers({ ...answers, [q.id]: { ...answers[q.id], text: e.target.value } })}
                      data-testid={`answer-text-${q.id}`} />
                    <div className="mt-2 flex flex-wrap items-center gap-3">
                      <Button size="sm" onClick={() => answer(q)} data-testid={`answer-send-${q.id}`}>
                        <Send className="h-4 w-4 me-1.5" />Send answer
                      </Button>
                      <div className="flex items-center gap-2 text-xs">
                        <Switch checked={!!answers[q.id]?.publish}
                          onCheckedChange={(v) => setAnswers({ ...answers, [q.id]: { ...answers[q.id], publish: v } })}
                          data-testid={`answer-publish-${q.id}`} />Publish as a ruling
                      </div>
                      {answers[q.id]?.publish && (
                        <Input placeholder="ruling slug" className="h-8 w-40 text-xs"
                          value={answers[q.id]?.slug || ""}
                          onChange={(e) => setAnswers({ ...answers, [q.id]: { ...answers[q.id], slug: e.target.value } })}
                          data-testid={`answer-slug-${q.id}`} />
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
};
